import { Box } from '@mui/material';
import { AnimatePresence, m as lazyMotion, type Variants } from 'motion/react';
import { useMemo } from 'react';

import LoadingBubble from '../../../components/Chat/_components/ChatBubbles/_components/LoadingBubble/index.ts';
import ScooprBubble from '../../../components/Chat/_components/ChatBubbles/_components/ScooprBubble/index.ts';
import { variants as bubbleVariants } from '../../../components/Chat/_components/ChatBubbles/_components/ScooprBubble/variants.ts';

import TimeWrapper from './TimeWrapper/index.ts';

const variants = {
  hidden: {},
  visible: {
    transition: {
      ease: 'easeInOut',
      when: 'beforeChildren',
      staggerChildren: 0.4,
    },
  },
  exit: {
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
      when: 'afterChildren',
      staggerChildren: 0.4,
    },
  },
} satisfies Variants;

// Strict typing for incoming messages
export type ButtonMessage = {
  type: 'button';
  key?: React.Key;
  renderContent: () => React.ReactNode;
};

export type TextMessage = {
  // defaults to 'scoopr' if not provided
  type?: 'scoopr';
  key?: React.Key;
  text: string;
};

export type Message = ButtonMessage | TextMessage;

// Internal prepared items that include timing and normalized fields
type LoaderItem = {
  type: 'loader';
  key: React.Key;
  delay: number;
};

type PreparedButtonItem = ButtonMessage & {
  key: React.Key;
  delay: number;
};

type PreparedTextItem = {
  type: 'scoopr';
  key: React.Key;
  delay: number;
  text: string;
};

type PreparedItem = LoaderItem | PreparedButtonItem | PreparedTextItem;

const prepareItems = (items: Message[]): PreparedItem[] => {
  const list: PreparedItem[] = [];
  items.forEach((m, index) => {
    // add loader bubble before scoopr bubbles
    if (m.type !== 'button') {
      list.push({
        type: 'loader',
        key: (m.key as React.Key) ?? index,
        delay: index * 1500,
      });
    }

    const base = {
      key: (m.key as React.Key) ?? index,
      delay: index * 1500 + (m.type !== 'button' ? 600 : 0),
    };

    if (m.type === 'button') {
      list.push({ ...m, ...base });
    } else {
      // default to 'scoopr' if not provided
      list.push({
        type: 'scoopr',
        text: m.text,
        ...base,
      });
    }
  });
  return list;
};

interface FakeBobProps {
  messages: Message[];
  className?: string;
}

const FakeBob = ({ messages, className = '' }: FakeBobProps) => {
  const preparedItems = useMemo(() => prepareItems(messages), [messages]);

  return (
    <AnimatePresence mode="wait">
      <Box
        component={lazyMotion.div}
        key="messages"
        className={className}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          margin: theme.spacing(4, 0, 2),
          width: '100%',
        })}
        variants={variants}
        animate="visible"
        initial="hidden"
        exit="exit"
        id="fake-bob-container"
      >
        {preparedItems.map(({ delay, key, type, ...rest }) => (
          <TimeWrapper key={key} delay={delay} destroy={type === 'loader'}>
            {type === 'loader' && <LoadingBubble loading />}
            {type === 'scoopr' && <ScooprBubble utterance={(rest as PreparedTextItem).text} />}
            {type === 'button' && (
              <lazyMotion.div variants={bubbleVariants}>
                {(rest as PreparedButtonItem).renderContent()}
              </lazyMotion.div>
            )}
          </TimeWrapper>
        ))}
      </Box>
    </AnimatePresence>
  );
};

export default FakeBob;
